// == responsive ==

"use client"
import { useState, useEffect, useRef } from "react"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { Menu, Bell } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { NotificationModal } from "./notification-modal"

interface HeaderProps {
  onSidebarToggle: () => void
}

export function Header({ onSidebarToggle }: HeaderProps) {
  const [notifications] = useState(5) // Mock notification count
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [userName, setUserName] = useState("John Doe") // Default fallback
  const [userPhoto, setUserPhoto] = useState<string | null>(null)
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Generate initials from username
  const getInitials = (name: string): string => {
    if (!name) return "JD"
    const words = name.trim().split(" ")
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase()
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }

  // Load user name/photo from sessionStorage/localStorage on component mount
  useEffect(() => {
    const storedUserName = sessionStorage.getItem("userName") || localStorage.getItem("userName")
    ////console.log("[Header] storedUserName from localStorage:", storedUserName)
    if (storedUserName) {
      setUserName(storedUserName)
    }
    const storedPhoto = sessionStorage.getItem("userPhoto")
    if (storedPhoto) {
      setUserPhoto(storedPhoto)
    }
  }, [])

  // Set up heartbeat interval for QA/Production environments
  useEffect(() => {
    const sysEnv = (process.env.REACT_APP_SYS_ENV || "").toLowerCase()
    
    // Only run heartbeat in QA/Production where MSAL is used
    if (sysEnv === "prod" || sysEnv === "qa") {
      // Heartbeat function to check session validity
      const checkHeartbeat = async () => {
        try {
          const response = await fetch('/Routing/hb', {
            method: 'GET',
            credentials: 'include', // Include cookies for session
          })
          
          if (!response.ok) {
            throw new Error('Heartbeat request failed')
          }

          const data = await response.json()
          
          if (!data.ok) {
            // Session expired, redirect to login
            //console.log("[Heartbeat] Session expired, redirecting to login")
            // Clear any stored user data
            sessionStorage.clear()
            localStorage.removeItem("userName")
            localStorage.removeItem("userEmail")
            localStorage.removeItem("roles")
            localStorage.removeItem("id")
            // Redirect to login
            window.location.href = '/'
          }
        } catch (error) {
          // On error, log but don't redirect (network issues shouldn't log user out)
          console.error("[Heartbeat] Error checking heartbeat:", error)
        }
      }

      // Check heartbeat immediately on mount
      checkHeartbeat()
      
      // Set up interval to check every 30 seconds
      heartbeatIntervalRef.current = setInterval(() => {
        checkHeartbeat()
      }, 30000) // 30 seconds

      // Cleanup interval on unmount
      return () => {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current)
        }
      }
    }
  }, [])

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 border-b px-4 py-3 h-16 bg-white border-gray-200`}
    >
      <div className="flex items-center justify-between h-full">
        {/* Left side */}
        <div className="flex items-center gap-4">
          {/* Always-visible Hamburger */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onSidebarToggle();
            }}
            className="p-2"
          >
            <Menu className={`w-5 h-5 text-gray-600`} />
          </Button>

          {/* Company Logo & Title */}
          <div className="flex items-center gap-3">
            <img
              src="/images/uci-logo.png"
              alt="UCI Logo"
              width={32}
              height={32}
              className="rounded-full"
            />
            <h1
              className={`text-base sm:text-lg font-semibold leading-tight
                whitespace-normal break-words max-w-[150px] sm:max-w-none
                text-gray-800`}
            >
              Timesheet Management System
            </h1>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Notifications */}
          {/* <Button
            variant="ghost"
            size="sm"
            className="p-2 relative"
            onClick={() => setIsNotificationOpen(true)}
          >
            <Bell className={`w-5 h-5 text-gray-600`} />
            {notifications > 0 && (
              <Badge className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs bg-red-500 text-white">
                {notifications}
              </Badge>
            )}
          </Button> */}

          {/* User Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-2 sm:px-3 py-2">
                <img
                  src={userPhoto || "/images/placeholder-user.jpg"}
                  alt="profile"
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span
                  className={`hidden sm:inline text-sm font-medium
                 text-gray-700`}
                >
                  {userName}
                </span>
              </Button>
            </DropdownMenuTrigger>
          </DropdownMenu>
        </div>
      </div>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />
    </header>
  )
}

